import { Injectable } from "@angular/core";
import { HttpRequest, HttpHeaders, HttpHandler, HttpEvent, HttpInterceptor, HTTP_INTERCEPTORS } from "@angular/common/http";

import { BehaviorSubject, Observable, throwError } from "rxjs";
import { AuthService } from '../services/auth.service'
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { Router } from '@angular/router';
import { NotificationService } from "src/app/notification.service";
import { AppConfig } from "src/app/configs/app.config";
import { SKIP_TOKEN } from "../context/skip-token.context";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  returnUrl;
  returnParams;

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private tostr: NotificationService,
    private router: Router
  ) { }

  public readonly BASE_API_URL: string = AppConfig.BASE_API_URL;
  public readonly BASE_ADSP_API_URL: string = AppConfig.BASE_ADSP_URL;
  public readonly BASE_BDSP_API_URL: string = AppConfig.BASE_BDSP_URL;
  public readonly BASE_TDSP_API_URL: string = AppConfig.BASE_TDSP_URL;
  public readonly BASE_DCO_API_URL: string = AppConfig.BASE_DCO_URL;
  public readonly BASE_AI_BOT_URL: string = AppConfig.BASE_AI_BOT_URL;

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if(!token) {
      if(req.url.includes('view-contract')) return req.clone({responseType: 'blob'})
      return req
    }
    if (req.url.includes('fetch-feed-preview')) {
      return req.clone({ setHeaders: { Authorization: "Bearer " + token }, responseType: 'text' })
    }
    if(req.url.includes("qlikcloud.com")) {
      let qlikIntegrationID = localStorage.getItem('qlikIntegrationID');
      let jwt_token = localStorage.getItem('webToken')
      const headers = new HttpHeaders({
        'Authorization': 'Bearer ' + jwt_token,
        'qlik-web-integration-id': qlikIntegrationID,
      });
      return req.clone({headers, responseType: 'text', withCredentials: true})
    } else if(req.url.includes("view-contract")) {
      return req.clone({ setHeaders: { Authorization: "Bearer " + token }, responseType: 'blob' })
    }
    return req.clone({ setHeaders: { Authorization: "Bearer " + token } });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

  if (request.context.get(SKIP_TOKEN)) {
    return next.handle(request);
  }
    // Add jwt token into headers if available in local
    let token = localStorage.getItem("token");
    // console.log('added token from token interceptor and cloning request')
    request = this.addToken(request, token);
    const BASE_URLS = [this.BASE_API_URL, this.BASE_ADSP_API_URL, this.BASE_BDSP_API_URL, this.BASE_TDSP_API_URL, this.BASE_DCO_API_URL];

    const excludeArray = [].concat(...BASE_URLS.map(baseUrl => [
      `${baseUrl}/insertion-order/bulk-edit-pixels/`,
      `${baseUrl}/insertion-order/bulk-edit-flights/`,
      `${baseUrl}/line-item/bulk-edit-basic/`,
      `${baseUrl}/line-item/bulk-edit-targeting/`,
    ]));
    excludeArray.push(`${this.BASE_AI_BOT_URL}/chatbot/`);

    function checkExludeurl() {
      let isExlude = [];
      excludeArray.map((item) => {
        const filterBy = new RegExp(
          "^" + `${item}*`.replace(/\*/g, ".*") + "$"
        ).test(request.url);
        isExlude.push(filterBy);
      });
      return isExlude.includes(true);
    }

    let isMatched = checkExludeurl();

    return next.handle(request).pipe(
      catchError(err => {
        // return throwError(err)
        if (!navigator.onLine || err.status === 0) {
          this.router.navigate(['/server-down']);
          return throwError(err);
        }
        if (err.status === 401) {
          // console.log('TI>>>', err)
          if (err.error.detail && err.error.detail.includes("Token is invalid or expired")) {
            // console.log('refresh token expired (TI)')
            this.authService.logoutUser(true)
            return throwError(err);
          }
          if (err.error.detail && err.error.detail.includes("No active account found with the given credentials")) {
            this.tostr.error(err.error.detail);
            this.authService.logoutUser();
            return throwError(err)
          }
          if (err.error.detail && err.error.detail.includes("Default password is not allowed")) {
            return throwError(err)
          }
          if (err.error.detail && err.error.detail.includes("Your account is blocked")) {
            this.tostr.error(err.error.detail);
            return throwError(err);
          }
          if (err.error.detail && err.error.detail.includes("Too many failed login attempts. Please try again after")) {
            this.tostr.error(err.error.detail);
            return throwError(err)
          }
          // if (err.error?.error) {
          //   return throwError(err);
          // }
          // else{
          //   this.tostr.error(err.error);
          //  }
          return this.handle401Error(request, next)
        }
        else if (err.status == 405) {
          this.tostr.error('Application error');
        } else {
          // console.log(err)
          if (isMatched) return throwError(err)

          if (err.error.apnx_error) {
            this.tostr.error(err.error.apnx_error);
          }
          else if (err.error.error) {
            this.tostr.error(err.error.error);
          }
          else if (err.error.detail) {
            this.tostr.error(err.error.detail);
          }
          else {
            this.tostr.error(err.error);
          }
          return throwError(err);
        }
      })
    );
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (this.isRefreshing) {
      // Refresh already in progress — wait for new token then retry
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(req, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshToken().pipe(
      switchMap((res) => {
        const token = res['access'];
        localStorage.setItem('token', token);
        this.isRefreshing = false;
        this.refreshTokenSubject.next(token);
        return next.handle(this.addToken(req, token));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.authService.logoutUser(true);
        return throwError(err);
      })
    );
  }
}

export const TokenInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: TokenInterceptor,
  multi: true,
};
